/**
 * Records that the consumer intentionally finished a planning path. Only a consumer's own session
 * records it, and only the first time (`completedAt` keeps its first value); advisors and wizards
 * viewing the flow never do. The save is awaited and rejects on failure, so the finishing screen
 * stays put. `firstCompletion` tells the caller whether to show the first-arrival celebration.
 */
import { useCallback, useState } from 'react';
import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { useConsumerPlan } from './use-consumer-plan';

export function useRecordFlowCompletion(clientId: string) {
  const isConsumerSession = useIsConsumerSession();
  const { plan, savePlan } = useConsumerPlan(clientId);
  const [isRecording, setIsRecording] = useState(false);

  const recordFlowCompletion = useCallback(
    async (alsoSave: Partial<ConsumerPlanState> = {}): Promise<{ firstCompletion: boolean }> => {
      if (!isConsumerSession) return { firstCompletion: false };
      const firstCompletion = !plan?.completedAt;
      setIsRecording(true);
      try {
        await savePlan({
          ...alsoSave,
          ...(firstCompletion && { completedAt: new Date().toISOString() }),
        });
      } finally {
        setIsRecording(false);
      }
      return { firstCompletion };
    },
    [isConsumerSession, plan?.completedAt, savePlan],
  );

  return { recordFlowCompletion, isRecording };
}
